import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader, PageContent } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Save, Trash2, Wifi, RotateCw } from "lucide-react";
import { toast } from "sonner";

const EVENT_TYPES = [
  ["rss_news", "Notizia RSS"],
  ["match_start", "Inizio partita"],
  ["half_time", "Fine primo tempo"],
  ["second_half_start", "Inizio secondo tempo"],
  ["match_end", "Fine partita"],
  ["goal", "Gol"],
  ["penalty", "Rigore"],
  ["red_card", "Cartellino rosso"],
  ["yellow_card", "Cartellino giallo"],
  ["var", "VAR"],
  ["fixture_schedule", "Palinsesto giornaliero"],
  ["pre_match", "Pre-partita"],
  ["lineups", "Formazioni ufficiali"],
  ["daily_recap", "Recap giornaliero"],
  ["standings", "Classifica aggiornata"],
];

const CLUB_FRIENDLIES = {
  id: "667",
  name: "Friendlies Clubs",
};

const DEFAULT_EDITORIAL_LIVE_FILTER = {
  enabled: true,
  full_live_league_ids: ["135", "137", "547"],
  italy_national_league_ids: ["5", "1"],
  uefa_club_league_ids: ["2", "3", "848"],
  friendly_league_ids: [CLUB_FRIENDLIES.id],
  blocked_league_ids: ["15"],
  italy_national_team_id: "",
  serie_a_team_ids: [],
  serie_a_teams: [],
  serie_a_season: "",
  serie_a_teams_updated_at: "",
  uefa_final_stages: ["final"],
};

const VARIABLE_HELP = "{title} {content} {summary} {source} {image} {channel} {hashtags} {emoji} {category} {author} {published_at} {competition} {team} {player} {minute} {score} {home_team} {away_team} {home_score} {away_score}";

const csv = (value) => Array.isArray(value) ? value.join(", ") : "";

const arr = (value) => (value || "")
  .split(",")
  .map((v) => v.trim())
  .filter(Boolean);

const parseButtons = (value) => (value || "")
  .split("\\n")
  .map((row) => row.trim())
  .filter(Boolean)
  .map((row) => {
    const [label, url] = row.split("|");
    return { text: (label || "").trim(), url: (url || "").trim() };
  })
  .filter((button) => button.text && button.url);

const buttonsText = (buttons) => Array.isArray(buttons)
  ? buttons.map((button) => `${button.text || ""}|${button.url || ""}`).join("\\n")
  : (buttons || "");

function Row({ title, meta, active, onToggle, onDelete, children }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-md p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="font-bold text-white truncate">{title}</div>
          {meta && <div className="text-xs text-slate-500 truncate">{meta}</div>}
        </div>
        <div className="flex items-center gap-2">
          {onToggle && <Switch checked={!!active} onCheckedChange={onToggle} />}
          {onDelete && <Button size="sm" variant="ghost" className="text-red-400 h-8" onClick={onDelete}><Trash2 size={14} /></Button>}
        </div>
      </div>
      {children}
    </div>
  );
}

export default function Automation() {
  const [providers, setProviders] = useState([]);
  const [sportsCompetitionsText, setSportsCompetitionsText] = useState("");
  const [sportsFiltersCompetitionsText, setSportsFiltersCompetitionsText] = useState("");
  const [topics, setTopics] = useState([]);
  const [rules, setRules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [preview, setPreview] = useState(null);
  const [channels, setChannels] = useState([]);
  const [sportsFilters, setSportsFilters] = useState({
    enabled: false,
    allowed_competitions: [],
    allowed_event_types: [],
    allowed_teams: [],
  });
  const [sportsConfig, setSportsConfig] = useState({
    enabled: true,
    provider_priority: [],
    allowed_competitions: [],
    allowed_event_types: [],
    allowed_teams: [],
    editorial_live_filter: DEFAULT_EDITORIAL_LIVE_FILTER,
    routes: [],
    limits: { max_events_per_poll: 50, max_publishes_per_cycle: 20 },
  });
  const [refreshingSerieATeams, setRefreshingSerieATeams] = useState(false);
  const [matchDayConfig, setMatchDayConfig] = useState({
    enabled: false,
    timezone: "Europe/Rome",
    fixture_sync_minutes: 15,
    lineup_poll_minutes: 3,
    schedule_publish_at: "08:00",
    recap_publish_at: "23:00",
    profiles: [],
  });
  const [telegramPromotion, setTelegramPromotion] = useState({
    enabled: false,
    title: "",
    text: "",
    telegram_link: "",
    top_separator: "",
    bottom_separator: "",
  });
  const [search, setSearch] = useState("");

  const blankProvider = { name: "", provider: "openai", model: "gpt-5.2", base_url: "", api_key: "", purpose: "ai", active: true };
  const blankTopic = { name: "", slug: "", sport: "calcio", keywords: "", default_channel_names: "", active: true };
  const blankRule = { name: "", field: "title", keywords: "", channel_names: "", priority: 50, active: true };
  const blankTemplate = { name: "", event_type: "rss_news", emoji: "⚽", header: "{emoji} <b>{title}</b>", custom_text: "{body}", source_label: "Fonte", call_to_action: "", hashtags: "", channel_username: "", footer: "", show_original_link: true, clean_urls: true, buttons: "📖 Leggi l'articolo completo|{link}", active: true };
  const [providerForm, setProviderForm] = useState(blankProvider);
  const [topicForm, setTopicForm] = useState(blankTopic);
  const [ruleForm, setRuleForm] = useState(blankRule);
  const [templateForm, setTemplateForm] = useState(blankTemplate);

  const parseLinesOnSave = (value) =>
    String(value || "")
      .split("\n")
      .map(line => line.trim())
      .filter(Boolean);

  const clubFriendliesEnabled = parseLinesOnSave(sportsFiltersCompetitionsText)
    .some(value => value === CLUB_FRIENDLIES.id || value.toLocaleLowerCase() === CLUB_FRIENDLIES.name.toLocaleLowerCase());

  const toggleClubFriendlies = (enabled) => {
    const values = parseLinesOnSave(sportsFiltersCompetitionsText).filter(
      value => value !== CLUB_FRIENDLIES.id && value.toLocaleLowerCase() !== CLUB_FRIENDLIES.name.toLocaleLowerCase()
    );
    if (enabled) values.push(CLUB_FRIENDLIES.id);
    setSportsFiltersCompetitionsText(values.join("\n"));
  };

  const load = useCallback(async () => {
    const [p, t, r, tm, l, c, sf, sc, promo, md] = await Promise.all([
      api.get("/automation/providers"),
      api.get("/automation/topics"),
      api.get("/automation/rules"),
      api.get("/automation/templates"),
      api.get("/automation/logs"),
      api.get("/telegram/channels"),
      api.get("/automation/sports-api/filters"),
      api.get("/automation/sports-api/config"),
      api.get("/automation/telegram-promotion"),
      api.get("/automation/match-day/config"),
    ]);
    setProviders(p.data || []);
    setTopics(t.data || []);
    setRules(r.data || []);
    setTemplates(tm.data || []);
    setLogs(l.data || []);
    setChannels(c.data || []);
    setSportsFilters(sf.data || {
      enabled: false,
      allowed_competitions: [],
      allowed_event_types: [],
      allowed_teams: [],
    });
    setSportsConfig(sc.data || {
      enabled: true,
      provider_priority: [],
      allowed_competitions: [],
      allowed_event_types: [],
      allowed_teams: [],
      editorial_live_filter: DEFAULT_EDITORIAL_LIVE_FILTER,
      routes: [],
      limits: { max_events_per_poll: 50, max_publishes_per_cycle: 20 },
    });
    setTelegramPromotion(promo.data || {
      enabled: false,
      title: "",
      text: "",
      telegram_link: "",
      top_separator: "",
      bottom_separator: "",
    });
    setMatchDayConfig(md.data || {
      enabled: false,
      timezone: "Europe/Rome",
      fixture_sync_minutes: 15,
      lineup_poll_minutes: 3,
      schedule_publish_at: "08:00",
      recap_publish_at: "23:00",
      profiles: [],
    });
    setSportsFiltersCompetitionsText((sf.data?.allowed_competitions || []).join("\n"));
    setSportsCompetitionsText((sc.data?.allowed_competitions || []).join("\n"));
  }, []);

  useEffect(() => { load(); }, [load]);

  const channelNames = useMemo(() => channels.map(c => c.name).join(", "), [channels]);

    const parseCompetitionLines = (value) =>
    String(value || "")
      .split("\n")
      .map(line => line.trim())
      .filter(Boolean);

const saveSportsConfig = async () => {
    try {
      const { data } = await api.patch("/automation/sports-api/config", { ...sportsConfig, allowed_competitions: parseLinesOnSave(sportsCompetitionsText) });
      setSportsConfig(data);
      toast.success("Configurazione API live salvata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio configurazione API");
    }
  };

  const updateRoute = (index, patchValue) => {
    setSportsConfig((current) => ({
      ...current,
      routes: (current.routes || []).map((route, i) => i === index ? { ...route, ...patchValue } : route),
    }));
  };

  const updateEditorialLiveFilter = (patchValue) => {
    setSportsConfig((current) => ({
      ...current,
      editorial_live_filter: {
        ...DEFAULT_EDITORIAL_LIVE_FILTER,
        ...(current.editorial_live_filter || {}),
        ...patchValue,
      },
    }));
  };

  const refreshSerieATeams = async () => {
    setRefreshingSerieATeams(true);
    try {
      const { data } = await api.post("/automation/sports-api/editorial-live/serie-a-teams/refresh");
      updateEditorialLiveFilter(data || {});
      toast.success(`${data?.serie_a_team_ids?.length || 0} squadre Serie A aggiornate`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiornamento squadre Serie A");
    } finally {
      setRefreshingSerieATeams(false);
    }
  };

  const addRoute = () => {
    setSportsConfig((current) => ({
      ...current,
      routes: [
        ...(current.routes || []),
        { channel_name: "", mode: "team", teams: [], competitions: [], event_types: [], active: true },
      ],
    }));
  };

  const removeRoute = (index) => {
    setSportsConfig((current) => ({
      ...current,
      routes: (current.routes || []).filter((_, i) => i !== index),
    }));
  };

  const updateMatchDayProfile = (index, patchValue) => {
    setMatchDayConfig(current => ({
      ...current,
      profiles: (current.profiles || []).map((profile, i) => i === index ? { ...profile, ...patchValue } : profile),
    }));
  };

  const addMatchDayProfile = () => {
    const used = new Set((matchDayConfig.profiles || []).map(profile => profile.channel_id));
    const channel = channels.find(item => !used.has(item.id));
    if (!channel) {
      toast.error("Tutti i canali disponibili hanno già un profilo");
      return;
    }
    setMatchDayConfig(current => ({
      ...current,
      profiles: [...(current.profiles || []), {
        channel_id: channel.id,
        enabled: true,
        league_ids: [],
        team_ids: [],
        schedule_enabled: true,
        pre_match_offsets: [60],
        lineups_enabled: true,
        recap_enabled: true,
        live_event_types: ["goal", "red_card", "half_time", "match_end"],
        detail_level: "important",
      }],
    }));
  };

  const removeMatchDayProfile = index => {
    setMatchDayConfig(current => ({
      ...current,
      profiles: (current.profiles || []).filter((_, i) => i !== index),
    }));
  };

  const saveMatchDayConfig = async () => {
    try {
      const { data } = await api.patch("/automation/match-day/config", matchDayConfig);
      setMatchDayConfig(data);
      toast.success("Configurazione Match Day salvata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio Match Day");
    }
  };

  const refreshMatchDay = async () => {
    try {
      const { data } = await api.post("/automation/match-day/refresh");
      toast.success(`Sincronizzazione completata: ${data?.sync?.fixtures || 0} fixture`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sincronizzazione Match Day fallita");
    }
  };

  const saveSportsFilters = async () => {
    try {
      const { data } = await api.patch("/automation/sports-api/filters", { ...sportsFilters, allowed_competitions: parseLinesOnSave(sportsFiltersCompetitionsText) });
      setSportsFilters(data);
      toast.success("Filtri API salvati");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio filtri API");
    }
  };

  const saveTelegramPromotion = async () => {
    try {
      const { data } = await api.patch("/automation/telegram-promotion", telegramPromotion);
      setTelegramPromotion(data);
      toast.success("Promozione Telegram salvata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio promozione");
    }
  };

  const filteredLogs = logs.filter(l => !search || JSON.stringify(l).toLowerCase().includes(search.toLowerCase()));

  const createProvider = async () => {
    await api.post("/automation/providers", providerForm);
    setProviderForm(blankProvider);
    toast.success("Provider aggiunto");
    load();
  };
  const createTopic = async () => {
    await api.post("/automation/topics", { ...topicForm, keywords: arr(topicForm.keywords), default_channel_names: arr(topicForm.default_channel_names) });
    setTopicForm(blankTopic);
    toast.success("Argomento aggiunto");
    load();
  };
  const createRule = async () => {
    await api.post("/automation/rules", { ...ruleForm, priority: parseInt(ruleForm.priority) || 50, match_type: "any_keyword", keywords: arr(ruleForm.keywords), channel_names: arr(ruleForm.channel_names) });
    setRuleForm(blankRule);
    toast.success("Regola aggiunta");
    load();
  };
  const createTemplate = async () => {
    await api.post("/automation/templates", { ...templateForm, hashtags: arr(templateForm.hashtags), buttons: parseButtons(templateForm.buttons) });
    setTemplateForm(blankTemplate);
    toast.success("Template aggiunto");
    load();
  };
  const patch = async (url, payload) => { await api.patch(url, payload); load(); };
  const remove = async (url) => { if (!window.confirm("Eliminare?")) return; await api.delete(url); load(); };

  const previewTemplate = async (template) => {
    try {
      const { data } = await api.post("/automation/templates/preview", { template });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore anteprima");
    }
  };

  return (
    <div data-testid="automation-page">
      <PageHeader title="Automazioni" subtitle="Configura provider API, routing, argomenti, template Telegram e log di pubblicazione.">
        <Button variant="outline" className="border-slate-700" onClick={async()=>{ await api.post("/automation/seed"); toast.success("Configurazione base aggiornata"); load(); }}><RotateCw size={16} className="mr-1" />Ripristina default mancanti</Button>
      </PageHeader>
      <PageContent>
        <Tabs defaultValue="providers">
          <TabsList className="bg-slate-900 border border-slate-800 flex-wrap h-auto">
            <TabsTrigger value="providers">API</TabsTrigger>
            <TabsTrigger value="topics">Argomenti</TabsTrigger>
            <TabsTrigger value="rules">Regole</TabsTrigger>
            <TabsTrigger value="templates">Template Telegram</TabsTrigger>
            <TabsTrigger value="promotion">Promozione finale</TabsTrigger>
            <TabsTrigger value="sports-api">Competizioni API</TabsTrigger>
            <TabsTrigger value="sports-control">API Live</TabsTrigger>
            <TabsTrigger value="match-day">Match Day</TabsTrigger>
            <TabsTrigger value="logs">Log</TabsTrigger>
          </TabsList>

          <TabsContent value="providers" className="mt-4 space-y-4">
            <div className="bg-slate-900 border border-slate-800 rounded-md p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><Label>Nome</Label><Input value={providerForm.name} onChange={e=>setProviderForm({...providerForm, name:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" /></div>
              <div><Label>Provider</Label><Input value={providerForm.provider} onChange={e=>setProviderForm({...providerForm, provider:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" /></div>
              <div><Label>Modello</Label><Input value={providerForm.model} onChange={e=>setProviderForm({...providerForm, model:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" /></div>
              <div><Label>Base URL</Label><Input value={providerForm.base_url} onChange={e=>setProviderForm({...providerForm, base_url:e.target.value})} className="bg-slate-950 border-slate-800 mt-1 font-mono text-xs" /></div>
              <div><Label>API Key</Label><Input type="password" value={providerForm.api_key} onChange={e=>setProviderForm({...providerForm, api_key:e.target.value})} className="bg-slate-950 border-slate-800 mt-1 font-mono text-xs" /></div>
              <div className="flex items-end"><Button onClick={createProvider} className="bg-blue-600 hover:bg-blue-700"><Plus size={15} className="mr-1" />Aggiungi provider</Button></div>
            </div>
            {providers.map(p => (
              <Row key={p.id} title={p.name} meta={`${p.provider} · ${p.model || "modello non impostato"}`} active={p.active} onToggle={v=>patch(`/automation/providers/${p.id}`, { active: v })} onDelete={()=>remove(`/automation/providers/${p.id}`)}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input defaultValue={p.name || ""} onBlur={e=>patch(`/automation/providers/${p.id}`, { name: e.target.value })} className="bg-slate-950 border-slate-800" placeholder="Nome provider" />
                  <Input defaultValue={p.provider || ""} onBlur={e=>patch(`/automation/providers/${p.id}`, { provider: e.target.value })} className="bg-slate-950 border-slate-800" placeholder="Provider" />
                  <Input defaultValue={p.model || ""} onBlur={e=>patch(`/automation/providers/${p.id}`, { model: e.target.value })} className="bg-slate-950 border-slate-800" placeholder="Modello" />
                  <Input defaultValue={p.base_url || ""} onBlur={e=>patch(`/automation/providers/${p.id}`, { base_url: e.target.value })} className="bg-slate-950 border-slate-800 font-mono text-xs" placeholder="Base URL" />
                  <Input type="password" defaultValue={p.api_key || ""} onBlur={e=>patch(`/automation/providers/${p.id}`, { api_key: e.target.value })} className="bg-slate-950 border-slate-800 font-mono text-xs md:col-span-2" placeholder="API Key" />
                  <Button size="sm" variant="outline" className="border-slate-700 w-fit" onClick={async()=>{ try { await api.post(`/automation/providers/${p.id}/test`); toast.success("Connessione valida"); } catch(e) { toast.error(e?.response?.data?.detail || "Test fallito"); } }}><Wifi size={14} className="mr-1" />Test connessione</Button>
                </div>
              </Row>
            ))}
          </TabsContent>

          <TabsContent value="topics" className="mt-4 space-y-4">
            <div className="bg-slate-900 border border-slate-800 rounded-md p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
              <Input placeholder="Nome" value={topicForm.name} onChange={e=>setTopicForm({...topicForm, name:e.target.value, slug: e.target.value.toLowerCase().replaceAll(" ", "-")})} className="bg-slate-950 border-slate-800" />
              <Input placeholder="Sport" value={topicForm.sport} onChange={e=>setTopicForm({...topicForm, sport:e.target.value})} className="bg-slate-950 border-slate-800" />
              <Input placeholder="Keyword, separate da virgola" value={topicForm.keywords} onChange={e=>setTopicForm({...topicForm, keywords:e.target.value})} className="bg-slate-950 border-slate-800 md:col-span-2" />
              <Button onClick={createTopic} className="bg-blue-600 hover:bg-blue-700"><Plus size={15} className="mr-1" />Aggiungi</Button>
              <Input placeholder={`Canali: ${channelNames || "CalcioNews24"}`} value={topicForm.default_channel_names} onChange={e=>setTopicForm({...topicForm, default_channel_names:e.target.value})} className="bg-slate-950 border-slate-800 md:col-span-5" />
            </div>
            {topics.map(t => (
              <Row key={t.id} title={t.name} meta={`${t.sport} · ${(t.default_channel_names || []).join(", ")}`} active={t.active} onToggle={v=>patch(`/automation/topics/${t.id}`, { active: v })} onDelete={()=>remove(`/automation/topics/${t.id}`)}>
                <Textarea value={csv(t.keywords)} onChange={e=>patch(`/automation/topics/${t.id}`, { keywords: arr(e.target.value) })} className="bg-slate-950 border-slate-800 text-xs" rows={2} />
              </Row>
            ))}
          </TabsContent>

          <TabsContent value="rules" className="mt-4 space-y-4">
            <div className="bg-slate-900 border border-slate-800 rounded-md p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
              <Input placeholder="Nome regola" value={ruleForm.name} onChange={e=>setRuleForm({...ruleForm, name:e.target.value})} className="bg-slate-950 border-slate-800" />
              <Input placeholder="Keyword titolo" value={ruleForm.keywords} onChange={e=>setRuleForm({...ruleForm, keywords:e.target.value})} className="bg-slate-950 border-slate-800 md:col-span-2" />
              <Input placeholder="Canali destinatari" value={ruleForm.channel_names} onChange={e=>setRuleForm({...ruleForm, channel_names:e.target.value})} className="bg-slate-950 border-slate-800" />
              <Button onClick={createRule} className="bg-blue-600 hover:bg-blue-700"><Plus size={15} className="mr-1" />Aggiungi</Button>
            </div>
            {rules.map(r => (
              <Row key={r.id} title={r.name} meta={`${r.field} contiene: ${(r.keywords || []).join(", ")}`} active={r.active} onToggle={v=>patch(`/automation/rules/${r.id}`, { active: v })} onDelete={()=>remove(`/automation/rules/${r.id}`)}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input value={csv(r.keywords)} onChange={e=>patch(`/automation/rules/${r.id}`, { keywords: arr(e.target.value) })} className="bg-slate-950 border-slate-800" />
                  <Input value={csv(r.channel_names)} onChange={e=>patch(`/automation/rules/${r.id}`, { channel_names: arr(e.target.value) })} className="bg-slate-950 border-slate-800" />
                </div>
              </Row>
            ))}
          </TabsContent>

          <TabsContent value="templates" className="mt-4 space-y-4">
            <div className="bg-blue-600/10 border border-blue-500/30 rounded-md p-4 text-sm text-blue-100">
              I link degli articoli RSS non vengono mai mostrati come URL nel testo. Il sistema aggiunge automaticamente il pulsante Telegram <b>📖 Leggi articolo</b> usando {"{link}"} pulito da parametri UTM.
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-md p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input placeholder="Nome template" value={templateForm.name} onChange={e=>setTemplateForm({...templateForm, name:e.target.value})} className="bg-slate-950 border-slate-800" />
              <select value={templateForm.event_type} onChange={e=>setTemplateForm({...templateForm, event_type:e.target.value})} className="bg-slate-950 border border-slate-800 rounded-md px-3 text-sm text-slate-100">
                {EVENT_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
              <Input placeholder="Emoji" value={templateForm.emoji} onChange={e=>setTemplateForm({...templateForm, emoji:e.target.value})} className="bg-slate-950 border-slate-800" />
              <Input placeholder="Hashtag separati da virgola" value={templateForm.hashtags} onChange={e=>setTemplateForm({...templateForm, hashtags:e.target.value})} className="bg-slate-950 border-slate-800" />
              <Input placeholder="Username canale, es. @MilanNews24" value={templateForm.channel_username} onChange={e=>setTemplateForm({...templateForm, channel_username:e.target.value})} className="bg-slate-950 border-slate-800" />
              <Input placeholder="Etichetta fonte" value={templateForm.source_label} onChange={e=>setTemplateForm({...templateForm, source_label:e.target.value})} className="bg-slate-950 border-slate-800" />
              <Textarea placeholder="Header HTML" value={templateForm.header} onChange={e=>setTemplateForm({...templateForm, header:e.target.value})} className="bg-slate-950 border-slate-800 md:col-span-3 font-mono text-xs" rows={3} />
              <Textarea placeholder={VARIABLE_HELP} value={templateForm.custom_text} onChange={e=>setTemplateForm({...templateForm, custom_text:e.target.value})} className="bg-slate-950 border-slate-800 md:col-span-3 font-mono text-xs" rows={4} />
              <Textarea placeholder="Pulsanti: testo|url, una riga per pulsante" value={templateForm.buttons} onChange={e=>setTemplateForm({...templateForm, buttons:e.target.value})} className="bg-slate-950 border-slate-800 md:col-span-3 font-mono text-xs" rows={2} />
              <div className="md:col-span-3 text-[11px] text-slate-500">
                Variabili disponibili: {VARIABLE_HELP}. Puoi usare HTML Telegram: &lt;b&gt;, &lt;i&gt;, &lt;a href=""&gt;.
              </div>
              <Button onClick={createTemplate} className="bg-blue-600 hover:bg-blue-700"><Save size={15} className="mr-1" />Salva template</Button>
            </div>

            {templates.map(t => (
              <Row key={t.id} title={t.name} meta={`${EVENT_TYPES.find(([value]) => value === t.event_type)?.[1] || t.event_type || "Notizia RSS"} · ${t.emoji || ""} ${(t.hashtags || []).join(" ")}`} active={t.active} onToggle={v=>patch(`/automation/templates/${t.id}`, { active: v })} onDelete={()=>remove(`/automation/templates/${t.id}`)}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input defaultValue={t.name || ""} onBlur={e=>patch(`/automation/templates/${t.id}`, { name: e.target.value })} className="bg-slate-950 border-slate-800" placeholder="Nome template" />
                  <select defaultValue={t.event_type || "rss_news"} onBlur={e=>patch(`/automation/templates/${t.id}`, { event_type: e.target.value })} className="bg-slate-950 border border-slate-800 rounded-md px-3 text-sm text-slate-100">
                    {EVENT_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                  <Input defaultValue={t.emoji || ""} onBlur={e=>patch(`/automation/templates/${t.id}`, { emoji: e.target.value })} className="bg-slate-950 border-slate-800" placeholder="Emoji" />
                  <Input defaultValue={t.channel_username || ""} onBlur={e=>patch(`/automation/templates/${t.id}`, { channel_username: e.target.value })} className="bg-slate-950 border-slate-800" placeholder="@Canale" />
                  <Input defaultValue={t.source_label || "Fonte"} onBlur={e=>patch(`/automation/templates/${t.id}`, { source_label: e.target.value })} className="bg-slate-950 border-slate-800" placeholder="Fonte" />
                  <Input defaultValue={(t.hashtags || []).join(", ")} onBlur={e=>patch(`/automation/templates/${t.id}`, { hashtags: arr(e.target.value) })} className="bg-slate-950 border-slate-800" placeholder="Hashtag" />
                  <Textarea defaultValue={t.header || ""} onBlur={e=>patch(`/automation/templates/${t.id}`, { header: e.target.value })} className="bg-slate-950 border-slate-800 md:col-span-2 font-mono text-xs" rows={3} placeholder="Header" />
                  <Textarea defaultValue={t.custom_text || ""} onBlur={e=>patch(`/automation/templates/${t.id}`, { custom_text: e.target.value })} className="bg-slate-950 border-slate-800 md:col-span-2 font-mono text-xs" rows={4} placeholder="Testo" />
                  <Textarea defaultValue={buttonsText(t.buttons) || "📖 Leggi articolo|{link}"} onBlur={e=>patch(`/automation/templates/${t.id}`, { buttons: parseButtons(e.target.value) })} className="bg-slate-950 border-slate-800 md:col-span-2 font-mono text-xs" rows={2} placeholder="Pulsanti" />
                  <Textarea defaultValue={t.footer || ""} onBlur={e=>patch(`/automation/templates/${t.id}`, { footer: e.target.value })} className="bg-slate-950 border-slate-800 md:col-span-2 font-mono text-xs" rows={2} placeholder="Footer" />
                  <label className="flex items-center justify-between bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm">
                    <span>Pulisci URL</span>
                    <Switch checked={t.clean_urls !== false} onCheckedChange={v=>patch(`/automation/templates/${t.id}`, { clean_urls: v })} />
                  </label>
                  <label className="flex items-center justify-between bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm">
                    <span>Mostra link nel testo</span>
                    <Switch checked={!!t.show_original_link} onCheckedChange={v=>patch(`/automation/templates/${t.id}`, { show_original_link: v })} />
                  </label>
                  <Button size="sm" variant="outline" className="border-slate-700 w-fit" onClick={()=>previewTemplate(t)}>Anteprima reale</Button>
                </div>
              </Row>
            ))}

            {preview && (
              <div className="bg-slate-900 border border-emerald-500/30 rounded-md p-4">
                <div className="text-xs uppercase tracking-wider font-mono text-emerald-400 mb-2">Anteprima Telegram reale</div>
                <div className="bg-slate-950 border border-slate-800 rounded-md p-4 whitespace-pre-wrap text-sm text-slate-100" dangerouslySetInnerHTML={{ __html: preview.text || "" }} />
                {(preview.reply_markup?.inline_keyboard || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {preview.reply_markup.inline_keyboard.flat().map((button, idx) => (
                      <a key={idx} href={button.url} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-md bg-blue-600 text-white text-sm">{button.text}</a>
                    ))}
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="promotion" className="mt-4 space-y-4">
            <div className="bg-slate-900 border border-slate-800 rounded-md p-4 space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-bold text-white">PROMOZIONE FINALE</div>
                  <div className="text-xs text-slate-500 mt-1">Blocco testuale aggiunto in fondo a RSS, API live, eventi e breaking news. Nessun pulsante inline.</div>
                </div>
                <Switch
                  checked={!!telegramPromotion.enabled}
                  onCheckedChange={v => setTelegramPromotion({ ...telegramPromotion, enabled: v })}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Titolo</Label>
                  <Input
                    value={telegramPromotion.title || ""}
                    onChange={e => setTelegramPromotion({ ...telegramPromotion, title: e.target.value })}
                    className="bg-slate-950 border-slate-800 mt-1"
                    placeholder="Titolo della promozione"
                  />
                </div>

                <div>
                  <Label>Link Telegram</Label>
                  <Input
                    value={telegramPromotion.telegram_link || ""}
                    onChange={e => setTelegramPromotion({ ...telegramPromotion, telegram_link: e.target.value })}
                    className="bg-slate-950 border-slate-800 mt-1 font-mono text-sm"
                    placeholder="https://t.me/xxxxx"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Separatore superiore</Label>
                  <Input
                    value={telegramPromotion.top_separator || ""}
                    onChange={e => setTelegramPromotion({ ...telegramPromotion, top_separator: e.target.value })}
                    className="bg-slate-950 border-slate-800 mt-1 font-mono text-sm"
                    placeholder="━━━━━━━━━━━━━━"
                  />
                </div>

                <div>
                  <Label>Separatore inferiore</Label>
                  <Input
                    value={telegramPromotion.bottom_separator || ""}
                    onChange={e => setTelegramPromotion({ ...telegramPromotion, bottom_separator: e.target.value })}
                    className="bg-slate-950 border-slate-800 mt-1 font-mono text-sm"
                    placeholder="━━━━━━━━━━━━━━"
                  />
                </div>
              </div>

              <div>
                <Label>Testo</Label>
                <Textarea
                  value={telegramPromotion.text || ""}
                  onChange={e => setTelegramPromotion({ ...telegramPromotion, text: e.target.value })}
                  className="bg-slate-950 border-slate-800 mt-1 font-mono text-xs"
                  rows={6}
                  placeholder={"Un solo canale.\nTutti i nostri contenuti."}
                />
              </div>

              <div className="bg-slate-950 border border-slate-800 rounded-md p-4 whitespace-pre-wrap text-sm text-slate-100">
                {[
                  telegramPromotion.top_separator,
                  telegramPromotion.title,
                  telegramPromotion.text,
                  telegramPromotion.telegram_link,
                  telegramPromotion.bottom_separator,
                ].filter(Boolean).join("\n\n") || "Anteprima promozione finale"}
              </div>

              <Button onClick={saveTelegramPromotion} className="bg-blue-600 hover:bg-blue-700">Salva promozione finale</Button>
            </div>
          </TabsContent>

          <TabsContent value="sports-api" className="mt-4 space-y-4">
            <div className="bg-slate-900 border border-slate-800 rounded-md p-4 space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-bold text-white">Filtro competizioni API</div>
                  <div className="text-xs text-slate-500 mt-1">Se attivo, API-Football pubblica solo le competizioni indicate.</div>
                </div>
                <Switch checked={!!sportsFilters.enabled} onCheckedChange={v=>setSportsFilters({...sportsFilters, enabled:v})} />
              </div>

              <div>
                <Label>Competizioni monitorate</Label>
                <Textarea
                  value={sportsFiltersCompetitionsText}
                  onChange={e => setSportsFiltersCompetitionsText(e.target.value)}
                  className="bg-slate-950 border-slate-800 mt-1 font-mono text-xs"
                  rows={8}
                  placeholder={"Serie A\nUEFA Champions League\nUEFA Europa League\nCoppa Italia"}
                />
                <div className="text-[11px] text-slate-500 mt-1">Una competizione per riga. Il confronto è case-insensitive e tollera match parziali.</div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-800 bg-slate-950 p-3">
                <div>
                  <div className="text-sm font-semibold text-white">Friendlies Clubs</div>
                  <div className="text-xs text-slate-500">API-Football league ID 667 · solo squadre presenti nella whitelist Serie A</div>
                </div>
                <Switch checked={clubFriendliesEnabled} onCheckedChange={toggleClubFriendlies} />
              </div>

              <div>
                <Label>Squadre monitorate opzionali</Label>
                <Textarea
                  value={(sportsFilters.allowed_teams || []).join("\n")}
                  onChange={e=>setSportsFilters({...sportsFilters, allowed_teams:e.target.value.split("\n").map(x=>x.trim()).filter(Boolean)})}
                  className="bg-slate-950 border-slate-800 mt-1 font-mono text-xs"
                  rows={5}
                  placeholder={"Milan\nInter\nJuventus\nNapoli"}
                />
              </div>

              <div>
                <Label>Tipi evento opzionali</Label>
                <Textarea
                  value={(sportsFilters.allowed_event_types || []).join("\n")}
                  onChange={e=>setSportsFilters({...sportsFilters, allowed_event_types:e.target.value.split("\n").map(x=>x.trim()).filter(Boolean)})}
                  className="bg-slate-950 border-slate-800 mt-1 font-mono text-xs"
                  rows={5}
                  placeholder={"match_start\ngoal\nhalf_time\nmatch_end\nred_card"}
                />
              </div>

              <Button onClick={saveSportsFilters} className="bg-blue-600 hover:bg-blue-700">Salva filtri API</Button>
            </div>
          </TabsContent>

          <TabsContent value="sports-control" className="mt-4 space-y-4">
            <div className="bg-slate-900 border border-slate-800 rounded-md p-4 space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-bold text-white">Centro controllo API live</div>
                  <div className="text-xs text-slate-500 mt-1">Gestisce eventi live API-Football, backup Football-Data e SportMonks opzionale.</div>
                </div>
                <Switch checked={sportsConfig.enabled !== false} onCheckedChange={v=>setSportsConfig({...sportsConfig, enabled:v})} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Priorità provider</Label>
                  <Textarea
                    value={(sportsConfig.provider_priority || []).join("\n")}
                    onChange={e=>setSportsConfig({...sportsConfig, provider_priority:e.target.value.split("\n").map(x=>x.trim()).filter(Boolean)})}
                    className="bg-slate-950 border-slate-800 mt-1 font-mono text-xs"
                    rows={4}
                    placeholder={"API-Football\nFootballData.org\nSportMonks"}
                  />
                </div>

                <div>
                  <Label>Tipi evento pubblicabili</Label>
                  <Textarea
                    value={(sportsConfig.allowed_event_types || []).join("\n")}
                    onChange={e=>setSportsConfig({...sportsConfig, allowed_event_types:e.target.value.split("\n").map(x=>x.trim()).filter(Boolean)})}
                    className="bg-slate-950 border-slate-800 mt-1 font-mono text-xs"
                    rows={4}
                    placeholder={"match_start\ngoal\nhalf_time\nmatch_end\nred_card"}
                  />
                </div>

                <div>
                  <Label>Competizioni monitorate</Label>
                  <Textarea
                    value={sportsCompetitionsText}
                    onChange={e => setSportsCompetitionsText(e.target.value)}
                    className="bg-slate-950 border-slate-800 mt-1 font-mono text-xs"
                    rows={6}
                    placeholder={"Serie A\nUEFA Champions League\nCoppa Italia"}
                  />
                </div>

                <div>
                  <Label>Squadre globali opzionali</Label>
                  <Textarea
                    value={(sportsConfig.allowed_teams || []).join("\n")}
                    onChange={e=>setSportsConfig({...sportsConfig, allowed_teams:e.target.value.split("\n").map(x=>x.trim()).filter(Boolean)})}
                    className="bg-slate-950 border-slate-800 mt-1 font-mono text-xs"
                    rows={6}
                    placeholder={"Milan\nInter\nJuventus\nNapoli"}
                  />
                </div>
              </div>

              <div className="border-t border-slate-800 pt-4 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-bold text-white">Filtro editoriale LIVE CalcioNews24</div>
                    <div className="text-xs text-slate-500 mt-1">Filtro preventivo basato esclusivamente sugli ID API-Football.</div>
                  </div>
                  <Switch
                    checked={sportsConfig.editorial_live_filter?.enabled !== false}
                    onCheckedChange={enabled => updateEditorialLiveFilter({ enabled })}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                  <div>
                    <Label>ID Nazionale italiana</Label>
                    <Input
                      value={sportsConfig.editorial_live_filter?.italy_national_team_id || ""}
                      onChange={e => updateEditorialLiveFilter({ italy_national_team_id: e.target.value })}
                      className="bg-slate-950 border-slate-800 mt-1"
                      placeholder="Team ID API-Football"
                    />
                  </div>
                  <div>
                    <Label>Stagione Serie A</Label>
                    <Input
                      type="number"
                      value={sportsConfig.editorial_live_filter?.serie_a_season || ""}
                      onChange={e => updateEditorialLiveFilter({ serie_a_season: e.target.value ? Number(e.target.value) : null })}
                      className="bg-slate-950 border-slate-800 mt-1"
                      placeholder="2026"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full border-slate-700"
                      onClick={refreshSerieATeams}
                      disabled={refreshingSerieATeams}
                    >
                      <RotateCw size={15} className={`mr-1 ${refreshingSerieATeams ? "animate-spin" : ""}`} />
                      Aggiorna squadre Serie A
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                  {[
                    ["full_live_league_ids", "LIVE completo", "135\n137\n547"],
                    ["italy_national_league_ids", "Competizioni Nazionale", "5\n1"],
                    ["uefa_club_league_ids", "Competizioni UEFA club", "2\n3\n848"],
                    ["blocked_league_ids", "Competizioni escluse", "15"],
                    ["serie_a_team_ids", "Squadre Serie A (team ID)", "ID, uno per riga"],
                  ].map(([key, label, placeholder]) => (
                    <div key={key}>
                      <Label>{label}</Label>
                      <Textarea
                        value={(sportsConfig.editorial_live_filter?.[key] || []).join("\n")}
                        onChange={e => updateEditorialLiveFilter({ [key]: parseLinesOnSave(e.target.value) })}
                        className="bg-slate-950 border-slate-800 mt-1 font-mono text-xs"
                        rows={4}
                        placeholder={placeholder}
                      />
                    </div>
                  ))}
                </div>

                <div className="text-xs text-slate-500">
                  Squadre sincronizzate: {sportsConfig.editorial_live_filter?.serie_a_team_ids?.length || 0}
                  {sportsConfig.editorial_live_filter?.serie_a_teams_updated_at
                    ? ` · Ultimo aggiornamento: ${new Date(sportsConfig.editorial_live_filter.serie_a_teams_updated_at).toLocaleString("it-IT")}`
                    : " · Mai aggiornate"}
                </div>
              </div>

              <div className="border-t border-slate-800 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-bold text-white">Routing canali</div>
                    <div className="text-xs text-slate-500">Configura quali eventi vanno su ogni canale.</div>
                  </div>
                  <Button type="button" variant="outline" className="border-slate-700" onClick={addRoute}>Aggiungi route</Button>
                </div>

                <div className="space-y-3">
                  {(sportsConfig.routes || []).map((route, index) => (
                    <div key={index} className="bg-slate-950 border border-slate-800 rounded-md p-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div className="flex items-center justify-between md:col-span-2">
                        <div className="text-sm font-semibold text-white">Route #{index + 1}</div>
                        <div className="flex items-center gap-2">
                          <Switch checked={route.active !== false} onCheckedChange={v=>updateRoute(index, { active: v })} />
                          <Button size="sm" variant="ghost" className="text-red-400 h-8" onClick={()=>removeRoute(index)}>Rimuovi</Button>
                        </div>
                      </div>

                      <div>
                        <Label>Canale</Label>
                        <select
                          value={route.channel_name || ""}
                          onChange={e=>updateRoute(index, { channel_name: e.target.value })}
                          className="mt-1 w-full h-10 rounded-md bg-slate-900 border border-slate-800 px-3 text-sm text-slate-100"
                        >
                          <option value="">Seleziona canale</option>
                          {channels.map(ch => <option key={ch.id} value={ch.name}>{ch.name}</option>)}
                        </select>
                      </div>

                      <div>
                        <Label>Modalità</Label>
                        <select
                          value={route.mode || "team"}
                          onChange={e=>updateRoute(index, { mode: e.target.value })}
                          className="mt-1 w-full h-10 rounded-md bg-slate-900 border border-slate-800 px-3 text-sm text-slate-100"
                        >
                          <option value="all">Tutto</option>
                          <option value="team">Squadre</option>
                          <option value="competition">Competizioni</option>
                        </select>
                      </div>

                      <div>
                        <Label>Squadre associate</Label>
                        <Textarea
                          value={(route.teams || []).join("\n")}
                          onChange={e=>updateRoute(index, { teams:e.target.value.split("\n").map(x=>x.trim()).filter(Boolean) })}
                          className="bg-slate-900 border-slate-800 mt-1 font-mono text-xs"
                          rows={4}
                          placeholder={"Milan\nAC Milan"}
                        />
                      </div>

                      <div>
                        <Label>Competizioni route</Label>
                        <Textarea
                          value={(route.competitions || []).join("\n")}
                          onChange={e=>updateRoute(index, { competitions:e.target.value.split("\n").map(x=>x.trim()).filter(Boolean) })}
                          className="bg-slate-900 border-slate-800 mt-1 font-mono text-xs"
                          rows={4}
                          placeholder={"Serie A\nUEFA Champions League"}
                        />
                      </div>

                      <div className="md:col-span-2">
                        <Label>Tipi evento route opzionali</Label>
                        <Input
                          value={(route.event_types || []).join(", ")}
                          onChange={e=>updateRoute(index, { event_types:e.target.value.split(",").map(x=>x.trim()).filter(Boolean) })}
                          className="bg-slate-900 border-slate-800 mt-1 font-mono text-xs"
                          placeholder="goal, red_card, match_end"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <Button onClick={saveSportsConfig} className="bg-blue-600 hover:bg-blue-700">Salva centro controllo API</Button>
            </div>
          </TabsContent>

          <TabsContent value="match-day" className="mt-4 space-y-4">
            <div className="bg-slate-900 border border-slate-800 rounded-md p-4 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="font-bold text-white">Match Day Engine V1</div>
                  <div className="text-xs text-slate-500 mt-1">Palinsesto, pre-match, formazioni e recap tramite il publisher Telegram esistente.</div>
                </div>
                <Switch checked={!!matchDayConfig.enabled} onCheckedChange={enabled => setMatchDayConfig({ ...matchDayConfig, enabled })} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                <div><Label>Timezone</Label><Input value={matchDayConfig.timezone || "Europe/Rome"} onChange={e => setMatchDayConfig({ ...matchDayConfig, timezone: e.target.value })} className="bg-slate-950 border-slate-800 mt-1" /></div>
                <div><Label>Palinsesto</Label><Input type="time" value={matchDayConfig.schedule_publish_at || "08:00"} onChange={e => setMatchDayConfig({ ...matchDayConfig, schedule_publish_at: e.target.value })} className="bg-slate-950 border-slate-800 mt-1" /></div>
                <div><Label>Recap</Label><Input type="time" value={matchDayConfig.recap_publish_at || "23:00"} onChange={e => setMatchDayConfig({ ...matchDayConfig, recap_publish_at: e.target.value })} className="bg-slate-950 border-slate-800 mt-1" /></div>
                <div><Label>Stagione API opzionale</Label><Input type="number" value={matchDayConfig.season || ""} onChange={e => setMatchDayConfig({ ...matchDayConfig, season: e.target.value ? Number(e.target.value) : null })} className="bg-slate-950 border-slate-800 mt-1" placeholder="Automatica" /></div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-800 pt-4">
                <div className="font-semibold text-white">Profili canale</div>
                <Button type="button" variant="outline" className="border-slate-700" onClick={addMatchDayProfile}><Plus size={15} className="mr-1" />Aggiungi profilo</Button>
              </div>

              <div className="space-y-3">
                {(matchDayConfig.profiles || []).map((profile, index) => {
                  const channel = channels.find(item => item.id === profile.channel_id);
                  return (
                    <div key={profile.channel_id || index} className="bg-slate-950 border border-slate-800 rounded-md p-3 space-y-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="font-semibold text-white">{channel?.name || "Canale non disponibile"}</div>
                        <div className="flex items-center gap-2">
                          <Switch checked={profile.enabled !== false} onCheckedChange={enabled => updateMatchDayProfile(index, { enabled })} />
                          <Button size="sm" variant="ghost" className="text-red-400" onClick={() => removeMatchDayProfile(index)}><Trash2 size={14} /></Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div><Label>Competizioni (league ID)</Label><Textarea value={(profile.league_ids || []).join("\n")} onChange={e => updateMatchDayProfile(index, { league_ids: parseLinesOnSave(e.target.value) })} rows={4} className="bg-slate-900 border-slate-800 mt-1 font-mono text-xs" placeholder={"135\n2\n137"} /></div>
                        <div><Label>Squadre (team ID)</Label><Textarea value={(profile.team_ids || []).join("\n")} onChange={e => updateMatchDayProfile(index, { team_ids: parseLinesOnSave(e.target.value) })} rows={4} className="bg-slate-900 border-slate-800 mt-1 font-mono text-xs" placeholder="ID API-Football, uno per riga" /></div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                        <label className="flex items-center gap-2 text-sm text-slate-300"><Switch checked={profile.schedule_enabled !== false} onCheckedChange={schedule_enabled => updateMatchDayProfile(index, { schedule_enabled })} />Palinsesto</label>
                        <label className="flex items-center gap-2 text-sm text-slate-300"><Switch checked={(profile.pre_match_offsets || []).includes(60)} onCheckedChange={checked => updateMatchDayProfile(index, { pre_match_offsets: checked ? [...new Set([...(profile.pre_match_offsets || []), 60])] : (profile.pre_match_offsets || []).filter(value => value !== 60) })} />Pre-match T-60</label>
                        <label className="flex items-center gap-2 text-sm text-slate-300"><Switch checked={(profile.pre_match_offsets || []).includes(15)} onCheckedChange={checked => updateMatchDayProfile(index, { pre_match_offsets: checked ? [...new Set([...(profile.pre_match_offsets || []), 15])] : (profile.pre_match_offsets || []).filter(value => value !== 15) })} />Reminder T-15</label>
                        <label className="flex items-center gap-2 text-sm text-slate-300"><Switch checked={profile.lineups_enabled !== false} onCheckedChange={lineups_enabled => updateMatchDayProfile(index, { lineups_enabled })} />Formazioni</label>
                        {!(profile.team_ids || []).length && (
                          <label className="flex items-center gap-2 text-sm text-slate-300"><Switch checked={profile.recap_enabled !== false} onCheckedChange={recap_enabled => updateMatchDayProfile(index, { recap_enabled })} />Recap giornaliero</label>
                        )}
                      </div>
                      <div>
                        <Label>Tipi evento Team Match Center</Label>
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
                          {[
                            ["match_start", "Inizio"],
                            ["goal", "Gol"],
                            ["yellow_card", "Cartellini"],
                            ["red_card", "Espulsioni"],
                            ["half_time", "Intervallo"],
                            ["match_end", "Fine partita"],
                            ...((profile.team_ids || []).length ? [
                              ["statistics", "Statistiche finali"],
                              ["match_report", "Resoconto AI"],
                              ["standings_update", "Classifica"],
                            ] : []),
                          ].map(([eventType, label]) => (
                            <label key={eventType} className="flex items-center gap-2 text-sm text-slate-300">
                              <Switch
                                checked={(profile.live_event_types || []).includes(eventType)}
                                onCheckedChange={checked => updateMatchDayProfile(index, {
                                  live_event_types: checked
                                    ? [...new Set([...(profile.live_event_types || []), eventType])]
                                    : (profile.live_event_types || []).filter(value => value !== eventType),
                                })}
                              />
                              {label}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="max-w-xs"><Label>Livello</Label><select value={profile.detail_level || "important"} onChange={e => updateMatchDayProfile(index, { detail_level: e.target.value })} className="mt-1 w-full h-10 rounded-md bg-slate-900 border border-slate-800 px-3 text-sm text-slate-100"><option value="important">Important</option><option value="detailed">Detailed</option></select></div>
                    </div>
                  );
                })}
              </div>

              <div className="flex flex-wrap gap-2">
                <Button onClick={saveMatchDayConfig} className="bg-blue-600 hover:bg-blue-700"><Save size={15} className="mr-1" />Salva Match Day</Button>
                <Button variant="outline" className="border-slate-700" onClick={refreshMatchDay}><RotateCw size={15} className="mr-1" />Sincronizza ora</Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="logs" className="mt-4 space-y-3">
            <Input placeholder="Cerca nei log" value={search} onChange={e=>setSearch(e.target.value)} className="bg-slate-900 border-slate-800 max-w-lg" />
            {filteredLogs.map(l => (
              <div key={l.id} className="bg-slate-900 border border-slate-800 rounded-md p-3 text-sm">
                <div className="flex justify-between gap-3">
                  <div className="font-semibold text-white">{l.event} {l.duplicate ? "· duplicato" : ""}</div>
                  <div className={l.success ? "text-emerald-400" : l.error ? "text-red-400" : "text-slate-500"}>{l.success ? "OK" : l.error || "INFO"}</div>
                </div>
                <div className="text-slate-400 mt-1">{l.article_title || "Nessun articolo"} · {l.feed_source || "feed n/d"} · {(l.channel_names || []).join(", ") || "nessun canale"}</div>
                <div className="text-[11px] text-slate-500 font-mono mt-1">{new Date(l.created_at).toLocaleString("it-IT")} · {l.filter_reason || ""}</div>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </PageContent>
    </div>
  );
}
